import { useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  Star, MapPin, Phone, Globe, Shield, Clock, CheckCircle2,
  Wrench, BadgePoundSterling, ChevronRight,
} from 'lucide-react'
import { usePlumber } from '../hooks/usePlumbers'
import { SUPPORT_PHONE_DISPLAY, SHOW_SUPPORT_PHONE } from '../lib/contact'
import { TextUsLink } from '../components/TextUsLink'
import { PageWrapper } from '../components/layout/PageWrapper'
import { PlumberProfileSkeleton } from '../components/ui/Skeletons'
import { Seo } from '../components/Seo'
import { MobileBookBar } from '../components/MobileBookBar'
import { logStatsigEvent } from '../lib/statsig'
import { goToEmergencyForm } from '../lib/goToEmergencyForm'

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const DEFAULT_BUSINESS_HOURS = [
  { day: 1, opensAt: '09:00', closesAt: '17:00' },
  { day: 2, opensAt: '09:00', closesAt: '17:00' },
  { day: 3, opensAt: '09:00', closesAt: '17:00' },
  { day: 4, opensAt: '09:00', closesAt: '17:00' },
  { day: 5, opensAt: '09:00', closesAt: '17:00' },
]

const STATUS_CONFIG = {
  available:   { label: 'Available now',     dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
  busy:        { label: 'Busy - may answer', dot: 'bg-amber-500',   text: 'text-amber-700',  bg: 'bg-amber-50 border-amber-200'    },
  unavailable: { label: 'Not available',     dot: 'bg-gray-400',    text: 'text-gray-600',   bg: 'bg-gray-50 border-gray-200'      },
}

export function PlumberProfile() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: plumber, isLoading } = usePlumber(id)

  // Top of the profile→booking funnel (see lib/statsig.ts).
  const plumberIdForView = plumber?._id
  useEffect(() => {
    if (!plumberIdForView) return
    logStatsigEvent('plumber_profile_viewed', plumberIdForView, { page: 'plumber-profile' })
  }, [plumberIdForView])

  if (isLoading) {
    return <PageWrapper hideCallBar><PlumberProfileSkeleton /></PageWrapper>
  }

  if (!plumber) {
    return (
      <PageWrapper>
        <div className="max-w-xl mx-auto px-4 py-16 text-center">
          <p className="text-gray-500">Plumber not found.</p>
        </div>
      </PageWrapper>
    )
  }

  const status = STATUS_CONFIG[plumber.availabilityStatus ?? 'unavailable']
  const schedule = plumber.workingHours?.schedule ?? []
  const usingAssumedHours = !plumber.workingHours?.alwaysOpen && schedule.length === 0
  const displaySchedule = usingAssumedHours ? DEFAULT_BUSINESS_HOURS : schedule

  const locationLabel = [plumber.city, plumber.postcode].filter(Boolean).join(', ')
  // Trim long company names so the title stays under ~60 chars ("too much text in title").
  const titleSuffix = plumber.city ? ` - Plumber in ${plumber.city}` : ' - Plumber'
  const capName = (n: string, max: number) => n.length <= max ? n
    : n.slice(0, max - 1).replace(/[\s,–-]+$/, '') + '…'
  const seoTitle = `${capName(plumber.companyName, Math.max(24, 60 - titleSuffix.length))}${titleSuffix}`

  // Canonical is the deep directory URL (keyword-rich, keeps /p/:id from
  // competing as a duplicate). Fall back to /p/:id if the plumber lacks slugs.
  const canonicalPath = (plumber.countrySlug && plumber.regionSlug && plumber.citySlug && plumber.postcodeArea && plumber.directorySlug)
    ? `/plumbers/${plumber.countrySlug}/${plumber.regionSlug}/${plumber.citySlug}/${plumber.postcodeArea.toLowerCase()}/${plumber.directorySlug}`
    : (id ? `/p/${id}` : undefined)

  const localBusinessLd = {
    '@context': 'https://schema.org',
    '@type': 'Plumber',
    name: plumber.companyName,
    ...(canonicalPath ? { url: `https://fixdelivered.com${canonicalPath}` } : {}),
    ...((plumber.address || plumber.city || plumber.postcode) && {
      address: {
        '@type': 'PostalAddress',
        ...(plumber.address ? { streetAddress: plumber.address } : {}),
        ...(plumber.city ? { addressLocality: plumber.city } : {}),
        ...(plumber.postcode ? { postalCode: plumber.postcode } : {}),
        addressCountry: 'GB',
      },
    }),
    areaServed: { '@type': 'Country', name: 'United Kingdom' },
    // No telephone here on purpose: this object IS the plumber, so the
    // property would assert our number is theirs. See profileJsonLd in
    // server/src/routes/ssrMeta.ts, and commit 404a96d, for why.
    ...(plumber.googleRating !== undefined && plumber.googleReviewCount !== undefined && plumber.googleReviewCount > 0 && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: plumber.googleRating,
        reviewCount: plumber.googleReviewCount,
      },
    }),
  }

  // Breadcrumbs (UK → county → town → company) when the plumber has the slugs.
  const breadcrumbs = (plumber.countrySlug && plumber.regionSlug && plumber.citySlug && canonicalPath?.startsWith('/plumbers'))
    ? [
        { name: 'UK', url: '/plumbers' },
        ...(plumber.region ? [{ name: plumber.region, url: `/plumbers/${plumber.countrySlug}/${plumber.regionSlug}` }] : []),
        ...(plumber.city ? [{ name: plumber.city, url: `/plumbers/${plumber.countrySlug}/${plumber.regionSlug}/${plumber.citySlug}` }] : []),
        { name: plumber.companyName, url: canonicalPath },
      ]
    : undefined

  return (
    <PageWrapper
      hideCallBar
      bottomBar={
        <MobileBookBar
          plumberId={plumber._id}
          companyName={plumber.companyName}
          rating={plumber.googleRating}
          reviewCount={plumber.googleReviewCount}
          emergencyAvailable={plumber.emergencyAvailable}
          page="plumber-profile"
        />
      }
    >
      <Seo
        title={seoTitle}
        description={`${plumber.companyName} - ${plumber.emergencyAvailable ? 'emergency ' : ''}plumber${locationLabel ? ` in ${locationLabel}` : ' in the UK'}${plumber.googleRating ? `, rated ${plumber.googleRating.toFixed(1)}/5` : ''}. Find and book a qualified local plumber with FixDelivered.`}
        path={canonicalPath}
        noBrandSuffix
        breadcrumbs={breadcrumbs}
        structuredData={localBusinessLd}
      />
      <div className="max-w-2xl mx-auto px-4 py-10 space-y-5">

        {/* ── Header ────────────────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              {plumber.emergencyAvailable && (
                <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-emergency-600 bg-emergency-50 border border-emergency-100 px-2 py-0.5 rounded-full mb-2">
                  <Wrench className="h-3 w-3" /> Emergency plumber
                </span>
              )}
              <h1 className="text-2xl font-bold text-gray-900">{plumber.companyName}</h1>
              {plumber.contactName && <p className="text-gray-500 mt-0.5">{plumber.contactName}</p>}
              <div className="flex items-center gap-1.5 text-sm text-gray-400 mt-2">
                <MapPin className="h-4 w-4 flex-shrink-0" />
                {[plumber.address, plumber.city, plumber.postcode].filter(Boolean).join(', ')}
              </div>
            </div>
            <div className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium ${status.bg} ${status.text}`}>
              <span className={`w-2 h-2 rounded-full ${status.dot}`} />
              {status.label}
            </div>
          </div>

          {plumber.googleRating !== undefined && (
            <div className="flex items-center gap-2 mt-4">
              <div className="flex">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className={`h-4 w-4 ${i <= Math.round(plumber.googleRating!) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`} />
                ))}
              </div>
              <span className="text-sm font-medium text-gray-700">{plumber.googleRating.toFixed(1)}</span>
              <span className="text-sm text-gray-400">· {plumber.googleReviewCount?.toLocaleString()} Google reviews</span>
            </div>
          )}

          <div className="flex flex-wrap gap-4 mt-4">
            {plumber.phone && (
              <a href={`tel:${plumber.phone}`} className="flex items-center gap-1.5 text-sm text-trust-500 hover:underline">
                <Phone className="h-4 w-4" />{plumber.phone}
              </a>
            )}
            {plumber.website && (
              <a href={plumber.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-trust-500 hover:underline">
                <Globe className="h-4 w-4" />Website
              </a>
            )}
          </div>
        </div>

        {/* ── Bio ───────────────────────────────────────────────────────────── */}
        {plumber.bio && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">About</h2>
            <p className="text-gray-700 leading-relaxed">{plumber.bio}</p>
          </div>
        )}

        {/* ── Stats ─────────────────────────────────────────────────────────── */}
        {plumber.callHistory && plumber.callHistory.totalConfirmed > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Track record via FixDelivered</h2>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-gray-900">{plumber.callHistory.totalConfirmed}</p>
                <p className="text-xs text-gray-500 mt-1">Jobs completed</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">£{plumber.callHistory.avgCalloutFee}</p>
                <p className="text-xs text-gray-500 mt-1">Avg callout fee</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{plumber.callHistory.avgEtaMinutes} min</p>
                <p className="text-xs text-gray-500 mt-1">Avg arrival</p>
              </div>
            </div>
          </div>
        )}

        {/* ── Qualifications & specialties ──────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Qualifications & services</h2>
          <div className="flex flex-wrap gap-2">
            {plumber.qualifications.map(q => (
              <span key={q} className="inline-flex items-center gap-1 text-xs font-medium bg-trust-50 text-trust-600 border border-trust-100 px-2.5 py-1 rounded-full">
                <Shield className="h-3 w-3" />{q}
              </span>
            ))}
            {plumber.trades.map(t => (
              <span key={t} className="inline-flex items-center gap-1 text-xs font-medium bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full capitalize">
                <Wrench className="h-3 w-3" />{t}
              </span>
            ))}
            {plumber.specialties?.map(s => (
              <span key={s} className="inline-flex items-center gap-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100 px-2.5 py-1 rounded-full">
                <CheckCircle2 className="h-3 w-3" />{s}
              </span>
            ))}
          </div>
        </div>

        {/* ── Coverage areas ─────────────────────────────────────────────────── */}
        {plumber.serviceAreas && plumber.serviceAreas.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Areas covered</h2>
            <div className="flex flex-wrap gap-2">
              {plumber.serviceAreas.map(area => (
                <span key={area} className="inline-flex items-center gap-1 text-xs font-medium bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">
                  <MapPin className="h-3 w-3" />{area}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ── Coverage & hours ──────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Coverage & hours</h2>
          <div className="flex items-center gap-2 text-sm text-gray-700 mb-4">
            <BadgePoundSterling className="h-4 w-4 text-gray-400" />
            Emergency response radius: <span className="font-semibold ml-1">{plumber.responseRadiusKm ?? 10} km</span>
          </div>
          {plumber.workingHours?.alwaysOpen ? (
            <div className="flex items-center gap-2 text-sm font-medium text-emerald-700">
              <Clock className="h-4 w-4" />Available 24 hours, 7 days a week
            </div>
          ) : (
            <div className="space-y-1">
              {displaySchedule.map(slot => (
                <div key={slot.day} className="flex items-center justify-between text-sm text-gray-600 py-1.5 border-b border-gray-50 last:border-0">
                  <span className="font-medium w-10">{DAY_NAMES[slot.day]}</span>
                  <span>{slot.opensAt} – {slot.closesAt}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Verification ──────────────────────────────────────────────────── */}
        <div className="flex items-center gap-2 text-xs text-gray-400 px-1">
          <Shield className="h-3.5 w-3.5" />
          Verified via {plumber.source === 'trustmark' ? 'TrustMark registered scheme' : 'WaterSafe approved contractor scheme'}
        </div>

        {/* ── Claim CTA ─────────────────────────────────────────────────────── */}
        {!plumber.isClaimed && (
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-5 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">Is this your business?</p>
              <p className="text-xs text-gray-500 mt-0.5">Claim this profile to manage your availability and details.</p>
            </div>
            <Link to={`/p/${plumber._id}/claim`} className="flex-shrink-0 flex items-center gap-1 text-sm font-semibold text-trust-500 hover:text-trust-600">
              Claim profile <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        )}

        {/* ── Owner portal link ─────────────────────────────────────────────── */}
        {plumber.isOwner && (
          <div className="bg-trust-50 rounded-2xl border border-trust-100 p-5 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">This is your profile</p>
              <p className="text-xs text-gray-600 mt-0.5">Manage availability, bio, and working hours.</p>
            </div>
            <Link to="/portal" className="flex-shrink-0 flex items-center gap-1 text-sm font-semibold text-trust-500 hover:text-trust-600">
              Open portal <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        )}

        {/* ── Emergency CTA ─────────────────────────────────────────────────── */}
        <div className="bg-[#0f172a] rounded-2xl p-6 text-center text-white">
          <p className="text-sm text-gray-300 mb-3">Need a plumber right now?</p>
          {/* Restored (issue #78) to the shape 404a96d removed - see the
              matching block in directory/DirectoryPlumber.tsx for the full
              reasoning and the caveat that came with it. */}
          {SHOW_SUPPORT_PHONE && (
            <TextUsLink
              onClick={() => logStatsigEvent('plumber_text_cta_clicked', plumber._id, { placement: 'cta-card', page: 'plumber-profile' })}
              className="flex items-center justify-center gap-2 bg-emergency-600 hover:bg-emergency-700 text-white font-bold px-6 py-3.5 rounded-xl transition-colors text-sm"
            >
              <Phone className="h-4 w-4" /> Text us now · {SUPPORT_PHONE_DISPLAY}
            </TextUsLink>
          )}
          <button
            type="button"
            onClick={() => {
              logStatsigEvent('plumber_book_cta_clicked', plumber._id, { placement: 'cta-card', page: 'plumber-profile' })
              goToEmergencyForm(navigate)
            }}
            className={SHOW_SUPPORT_PHONE
              ? 'inline-flex items-center gap-1 mt-3 text-sm font-semibold text-gray-300 hover:text-white transition-colors'
              : 'flex w-full items-center justify-center gap-2 bg-emergency-600 hover:bg-emergency-700 text-white font-bold px-6 py-3.5 rounded-xl transition-colors text-sm'}
          >
            {SHOW_SUPPORT_PHONE ? 'Or get emergency help online' : 'Get emergency help now'} <ChevronRight className="h-4 w-4" />
          </button>
          <p className="text-xs text-gray-500 mt-3">We'll find and confirm the fastest available plumber near you.</p>
        </div>

      </div>
    </PageWrapper>
  )
}
